import { Contract, formatEther, parseEther, getAddress, Wallet, ethers, getBytes } from "ethers";
import type { BrowserProvider, Signer } from "ethers";
import {
  ApplyVerificationResult,
  LoanStatus,
  type ContractConstants,
  type LoanApplicationDetails,
} from "../types/contract";

// Import the ABI
import contractABI from "../abi/MockDeFiLendingProtocol.abi.json";
import collateralContractABI from "../abi/Collateral.abi.json";
import verificationOracleContractABI from "../abi/VerificationOracle.abi.json";

export class MockDeFiLendingProtocolService {
  private contract: Contract | null = null;
  private signer: Signer | null = null;
  private collateralContract: Contract | null = null;
  private verificationOracleContract: Contract | null = null;

  constructor(
    private contractAddress: string,
    private collateralTokenAddress: string,
    private verificationOracleAddress: string,
  ) { }

  // Initialize the service with provider
  async initialize(provider: BrowserProvider) {
    try {
      this.signer = await provider.getSigner();
      this.contract = new Contract(
        this.contractAddress,
        contractABI,
        this.signer,
      );
      this.collateralContract = new Contract(
        this.collateralTokenAddress,
        collateralContractABI,
        this.signer,
      );
      this.verificationOracleContract = new Contract(
        this.verificationOracleAddress,
        verificationOracleContractABI,
        this.signer,
      );
    } catch (error) {
      // If there's an issue with the provider (like ENS resolution),
      // try to create the contract with a simpler approach
      console.warn(
        "Provider initialization failed, attempting fallback:",
        error,
      );
      throw error;
    }
  }

  // Check if service is initialized
  private checkInitialized() {
    if (!this.contract || !this.signer) {
      throw new Error("Service not initialized. Call initialize() first.");
    }
  }

  // Helper method to estimate gas with error handling
  private async estimateGasWithFallback(
    methodName: string,
    args: unknown[],
    overrides: Record<string, unknown> = {},
  ): Promise<bigint> {
    try {
      if (!this.contract) throw new Error("Contract not initialized");
      const gasEstimate = await this.contract[methodName].estimateGas(
        ...args,
        overrides,
      );
      // Add 20% buffer to the gas estimate
      return (gasEstimate * BigInt(120)) / BigInt(100);
    } catch (error) {
      console.warn(`Gas estimation failed for ${methodName}:`, error);
      // Fallback to a reasonable default gas limit
      return BigInt(300000); // 300k gas as fallback
    }
  }

  // Helper method to check if transaction will succeed before sending
  private async simulateTransaction(
    methodName: string,
    args: unknown[],
    overrides: Record<string, unknown> = {},
  ): Promise<void> {
    try {
      if (!this.contract) throw new Error("Contract not initialized");
      await this.contract[methodName].staticCall(...args, overrides);
    } catch (error: unknown) {
      // Parse the error to provide more meaningful feedback
      const errorMessage = this.parseContractError(error);
      throw new Error(errorMessage);
    }
  }

  // Helper method to parse contract errors into user-friendly messages
  private parseContractError(error: unknown): string {
    const err = error as {
      reason?: string;
      message?: string;
      data?: { message?: string };
      code?: string;
    };

    // Common contract error patterns
    if (err.reason) {
      switch (err.reason) {
        case "InsufficientCollateral":
          return "Insufficient collateral provided for the loan amount";
        case "LoanNotFound":
          return "No active loan application found";
        case "LoanAlreadyExists":
          return "You already have an active loan application";
        case "VerificationExpired":
          return "Your identity verification has expired";
        case "UnauthorizedAccess":
          return "You don't have permission to perform this action";
        case "InsufficientFunds":
          return "Insufficient funds to complete the transaction";
        default:
          return `Transaction will fail: ${err.reason}`;
      }
    }

    // Check for gas estimation errors
    if (err.message?.includes("gas")) {
      return "Transaction requires more gas than available or gas estimation failed";
    }

    // Check for insufficient balance errors
    if (
      err.message?.includes("insufficient funds") ||
      err.message?.includes("balance")
    ) {
      return "Insufficient balance to complete the transaction";
    }

    // Generic error fallback
    if (err.message) {
      return `Transaction error: ${err.message}`;
    }

    return "Transaction simulation failed - please check your inputs and try again";
  }

  // Read-only functions
  async getContractConstants(): Promise<ContractConstants> {
    this.checkInitialized();
    const contract = this.contract;
    if (!contract) throw new Error("Contract not initialized");

    const [
      maxVerificationAge,
      minLoanAmount,
      minCollateralRatio,
      defaultAdminRole,
      humanityOracleRole,
      upgraderRole,
      requiredClaims,
    ] = await Promise.all([
      contract.MAX_VERIFICATION_AGE(),
      contract.MIN_LOAN_AMOUNT(),
      contract.MIN_COLLATERAL_RATIO(),
      contract.DEFAULT_ADMIN_ROLE(),
      contract.HUMANITY_ORACLE_ROLE(),
      contract.UPGRADER_ROLE(),
      this.getRequiredClaims(),
    ]);

    return {
      MAX_VERIFICATION_AGE: maxVerificationAge,
      MIN_LOAN_AMOUNT: minLoanAmount,
      MIN_COLLATERAL_RATIO: minCollateralRatio,
      DEFAULT_ADMIN_ROLE: defaultAdminRole,
      HUMANITY_ORACLE_ROLE: humanityOracleRole,
      UPGRADER_ROLE: upgraderRole,
      REQUIRED_CLAIMS: requiredClaims,
    };
  }

  // async getDappDebt(): Promise<bigint> {
  // 	this.checkInitialized();
  // 	if (!this.verificationOracleContract) throw new Error("Contract not initialized");
  // 	return await this.verificationOracleContract.dappDebts(this.contractAddress);
  // }

  async getResult(resultId: string): Promise<ApplyVerificationResult> {
    this.checkInitialized();
    if (!this.verificationOracleContract) throw new Error("Contract not initialized");
    return await this.verificationOracleContract.getResult(resultId);
  }

  async getLoanApplication(borrower: string): Promise<LoanApplicationDetails> {
    this.checkInitialized();
    if (!this.contract) throw new Error("Contract not initialized");

    // Ensure the borrower address is properly formatted to avoid ENS resolution
    // Remove any potential whitespace and ensure proper checksum
    const cleanAddress = borrower.trim();
    let formattedAddress: string;

    try {
      formattedAddress = getAddress(cleanAddress);
    } catch {
      // If getAddress fails, use the address as-is (might be needed for some edge cases)
      formattedAddress = cleanAddress;
    }

    // Use loanApplications mapping to get full details including verificationRequestId
    const result = await this.contract.loanApplications(formattedAddress);

    return {
      amount: result[1],
      collateral: result[2],
      isVerified: result[3],
      status: result[5],
      timestamp: result[6],
      expiresAt: result[7],
      verificationRequestId: result[4], // verificationRequestId is at index 4
    };
  }

  async getHumanityOracle(): Promise<string> {
    this.checkInitialized();
    if (!this.contract) throw new Error("Contract not initialized");
    return await this.contract.humanityOracle();
  }

  async getCollateralToken(): Promise<string> {
    this.checkInitialized();
    if (!this.contract) throw new Error("Contract not initialized");
    return await this.contract.collateralToken();
  }

  async getCollateralBalance(address: string): Promise<bigint> {
    this.checkInitialized();
    if (!this.collateralContract)
      throw new Error("Collateral contract not initialized");

    // Ensure the address is properly formatted
    const cleanAddress = address.trim();
    let formattedAddress: string;

    try {
      formattedAddress = getAddress(cleanAddress);
    } catch {
      formattedAddress = cleanAddress;
    }

    return await this.collateralContract.balanceOf(formattedAddress);
  }

  async getVerificationFee(): Promise<bigint> {
    this.checkInitialized();
    if (!this.verificationOracleContract)
      throw new Error("Verification oracle contract not initialized");

    return await this.verificationOracleContract.verificationFee();
  }

  async getTreasuryAddress(): Promise<string> {
    this.checkInitialized();
    if (!this.verificationOracleContract)
      throw new Error("Verification oracle contract not initialized");

    return await this.verificationOracleContract.treasuryAddress();
  }

  async getTotalFeesCollected(): Promise<bigint> {
    this.checkInitialized();
    if (!this.verificationOracleContract)
      throw new Error("Verification oracle contract not initialized");

    return await this.verificationOracleContract.totalFeesCollected();
  }

  async getRequiredClaims(): Promise<string[]> {
    this.checkInitialized();
    if (!this.contract) throw new Error("Contract not initialized");
    const claims: string[] = [];
    let index = 0;

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const claim = await this.contract.REQUIRED_CLAIMS(index);
        claims.push(claim);
        index++;
      }
    } catch {
      // Array access out of bounds, we've got all claims
    }

    return claims;
  }

  async isUserVerified(
    userAddress: string,
    requiredClaims: string[],
    maxAge: number = 86400, // Default 24 hours in seconds
  ): Promise<{
    verified: boolean;
    attestationUID: string;
    expiresAt: bigint;
  }> {
    this.checkInitialized();
    if (!this.verificationOracleContract)
      throw new Error("Verification Oracle contract not initialized");

    // Ensure the address is properly formatted
    const cleanAddress = userAddress.trim();
    let formattedAddress: string;

    try {
      formattedAddress = getAddress(cleanAddress);
    } catch {
      throw new Error(`Invalid address format: ${cleanAddress}`);
    }


    const result = await this.verificationOracleContract.isUserVerified(
      formattedAddress,
      requiredClaims,
      BigInt(maxAge),
    );

    console.log(
      "🚀 ~ MockDeFiLendingProtocolService ~ isUserVerified ~ result:",
      result,
    );

    return {
      verified: result[0],
      attestationUID: result[1],
      expiresAt: result[2],
    };
  }

  // Write functions
  async applyForLoan(
    amount: string,
    collateral: string,
    verificationFee: bigint,
  ) {

    this.checkInitialized();
    if (!this.contract || !this.signer || !this.collateralContract) throw new Error("Contract not initialized");

    const amountWei = parseEther(amount);
    const collateralWei = parseEther(collateral);
    const feeWei = verificationFee;


    // Get user address for signature generation
    const userAddress = await this.signer.getAddress();



    // Check prerequisites before proceeding

    // 1. Check collateral token balance
    const collateralBalance = await this.collateralContract.balanceOf(userAddress);
    if (collateralBalance < collateralWei) {
      throw new Error(`Insufficient collateral tokens. Need ${formatEther(collateralWei)} but have ${formatEther(collateralBalance)}`);
    }

    // 2. Check collateral allowance
    const currentAllowance = await this.collateralContract.allowance(userAddress, this.contract.target);
    if (currentAllowance < collateralWei) {
      throw new Error(`Insufficient collateral allowance. Need to approve ${formatEther(collateralWei)} tokens but only ${formatEther(currentAllowance)} approved`);
    }

    // 3. Check ETH balance for verification fee
    const ethBalance = await this.signer.provider?.getBalance(userAddress);
    if (ethBalance && ethBalance < feeWei) {
      throw new Error(`Insufficient ETH balance for verification fee. Need ${formatEther(feeWei)} but have ${formatEther(ethBalance)}`);
    }


    let userSignature: string;
    try {
      // Generate user signature using the contract's helper function
      const messageHash = await this.contract.getPermissionMessageHashForLoan(userAddress);
      // Sign the raw message hash - signMessage() will add the eth prefix automatically
      userSignature = await this.signer.signMessage(getBytes(messageHash));
    } catch (signatureError: unknown) {
      const error = signatureError as { message?: string };
      throw new Error(`Failed to generate user signature: ${error.message || 'Unknown error'}`);
    }

    const args = [amountWei, collateralWei, userSignature];
    const overrides = { value: feeWei };


    try {
      // Simulate the transaction first to check if it will succeed
      await this.simulateTransaction("applyForLoan", args);
    } catch (simulationError: unknown) {
      const error = simulationError as { message?: string; reason?: string; code?: string };
      throw new Error(`Transaction simulation failed: ${error.message || 'Unknown error'}`);
    }

    try {
      // Estimate gas and add it to overrides
      const gasLimit = await this.estimateGasWithFallback(
        "applyForLoan",
        args,
        //overrides,
      );

      const tx = await this.contract.applyForLoan(amountWei, collateralWei, userSignature, {
        //...overrides,
        gasLimit,
      });

      return tx;
    } catch (txError: unknown) {
      const error = txError as { message?: string; reason?: string; code?: string; data?: any };
      throw new Error(`Transaction failed: ${error.message || 'Unknown error'}`);
    }
  }

  async repayLoan(amount: string) {
    this.checkInitialized();
    if (!this.contract) throw new Error("Contract not initialized");

    const amountWei = parseEther(amount);
    const args: unknown[] = [];
    const overrides = { value: amountWei };

    // Simulate the transaction first to check if it will succeed
    await this.simulateTransaction("repayLoan", args, overrides);

    // Estimate gas and add it to overrides
    const gasLimit = await this.estimateGasWithFallback(
      "repayLoan",
      args,
      overrides,
    );

    const tx = await this.contract.repayLoan({
      ...overrides,
      gasLimit,
    });

    return tx;
  }

  async approveCollateralTokens(amount: string) {
    this.checkInitialized();
    if (!this.collateralContract || !this.contract) throw new Error("Contracts not initialized");

    const amountWei = parseEther(amount);

    try {
      // Estimate gas for approve
      const gasEstimate = await this.collateralContract.approve.estimateGas(
        this.contract.target,
        amountWei
      );
      // Add 20% buffer
      const gasLimit = (gasEstimate * BigInt(120)) / BigInt(100);

      const tx = await this.collateralContract.approve(this.contract.target, amountWei, {
        gasLimit,
      });

      return tx;
    } catch (error) {
      console.warn("Gas estimation failed for approve, using fallback:", error);			// Fallback with default gas limit
      const tx = await this.collateralContract.approve(this.contract.target, amountWei, {
        gasLimit: BigInt(100000), // 100k gas as fallback
      });
      return tx;
    }
  }

  async mintCollateralTokens(toAddress: string, amount: string) {
    this.checkInitialized();
    if (!this.collateralContract) throw new Error("Collateral contract not initialized");

    const amountWei = parseEther(amount);

    try {
      // Get the provider from the current signer
      const provider = this.signer?.provider as BrowserProvider;
      if (!provider) {
        throw new Error("Provider not available");
      }

      // Create collateral contract instance with admin signer
      const adminCollateralContract = new Contract(
        this.collateralContract.target as string,
        collateralContractABI,
        this.signer,
      );

      // Estimate gas using the admin collateral contract
      const gasEstimate = await adminCollateralContract.mint.estimateGas(
        toAddress,
        amountWei,
      );
      // Add 20% buffer to the gas estimate
      const gasLimit = (gasEstimate * BigInt(120)) / BigInt(100);

      const tx = await adminCollateralContract.mint(toAddress, amountWei, {
        gasLimit,
      });

      return tx;
    } catch (error) {
      console.warn("Gas estimation failed for mint, using fallback:", error);

      // Get the provider from the current signer for fallback
      const provider = this.signer?.provider as BrowserProvider;
      if (!provider) {
        throw new Error("Provider not available");
      }

      // Create collateral contract instance with admin signer
      const adminCollateralContract = new Contract(
        this.collateralContract.target as string,
        collateralContractABI,
        this.signer,
      );

      // Fallback with default gas limit
      const tx = await adminCollateralContract.mint(toAddress, amountWei, {
        gasLimit: BigInt(200000), // 200k gas as fallback for mint
      });
      return tx;
    }
  }

  async updateRequiredClaims(claims: string[]) {
    this.checkInitialized();
    if (!this.contract) throw new Error("Contract not initialized");

    const args = [claims];
    const overrides = {};

    // Simulate the transaction first
    await this.simulateTransaction("updateRequireClaim", args, overrides);

    // Estimate gas
    const gasLimit = await this.estimateGasWithFallback(
      "updateRequireClaim",
      args,
      overrides,
    );

    const tx = await this.contract.updateRequireClaim(claims, { gasLimit });
    return tx;
  }

  async withdrawFunds() {
    this.checkInitialized();
    if (!this.contract) throw new Error("Contract not initialized");

    const args: unknown[] = [];
    const overrides = {};

    // Simulate the transaction first
    await this.simulateTransaction("withdrawFunds", args, overrides);

    // Estimate gas
    const gasLimit = await this.estimateGasWithFallback(
      "withdrawFunds",
      args,
      overrides,
    );

    const tx = await this.contract.withdrawFunds({ gasLimit });
    return tx;
  }

  async grantRole(role: string, account: string) {
    this.checkInitialized();
    if (!this.contract) throw new Error("Contract not initialized");

    const args = [role, account];
    const overrides = {};

    // Simulate the transaction first
    await this.simulateTransaction("grantRole", args, overrides);

    // Estimate gas
    const gasLimit = await this.estimateGasWithFallback(
      "grantRole",
      args,
      overrides,
    );

    const tx = await this.contract.grantRole(role, account, { gasLimit });
    return tx;
  }

  async revokeRole(role: string, account: string) {
    this.checkInitialized();
    if (!this.contract) throw new Error("Contract not initialized");

    const args = [role, account];
    const overrides = {};

    // Simulate the transaction first
    await this.simulateTransaction("revokeRole", args, overrides);

    // Estimate gas
    const gasLimit = await this.estimateGasWithFallback(
      "revokeRole",
      args,
      overrides,
    );

    const tx = await this.contract.revokeRole(role, account, { gasLimit });
    return tx;
  }

  async revokeVerificationRequest(attestationUID: string) {
    this.checkInitialized();
    if (!this.contract) throw new Error("Contract not initialized");

    try {
      // Estimate gas using the dapp contract directly
      const gasEstimate = await this.contract.revokeVerificationRequest.estimateGas(
        attestationUID,
      );
      // Add 20% buffer to the gas estimate
      const gasLimit = (gasEstimate * BigInt(120)) / BigInt(100);

      const tx = await this.contract.revokeVerificationRequest(attestationUID, {
        gasLimit,
      });

      return tx;
    } catch (error) {
      console.warn("Gas estimation failed for revokeVerificationRequest, using fallback:", error);
      // Fallback with default gas limit
      const tx = await this.contract.revokeVerificationRequest(attestationUID, {
        gasLimit: BigInt(300000), // 300k gas as fallback
      });
      return tx;
    }
  }

  // Utility functions
  formatEther(wei: bigint): string {
    return formatEther(wei);
  }

  parseEther(ether: string): bigint {
    return parseEther(ether);
  }

  getLoanStatusText(status: LoanStatus): string {
    const statusMap = {
      [LoanStatus.Pending]: "Pending",
      [LoanStatus.VerificationRequested]: "Verification Requested",
      [LoanStatus.VerificationCompleted]: "Verification Completed",
      [LoanStatus.Approved]: "Approved",
      [LoanStatus.Rejected]: "Rejected",
      [LoanStatus.Repaid]: "Repaid",
    };

    return statusMap[status] || "Unknown";
  }

  // Event listeners
  onLoanApplicationSubmitted(
    callback: (borrower: string, amount: bigint, collateral: bigint) => void,
  ) {
    this.checkInitialized();
    if (!this.contract) throw new Error("Contract not initialized");

    this.contract.on("LoanApplicationSubmitted", callback);
  }

  onVerificationRequested(
    callback: (borrower: string, requestId: string) => void,
  ) {
    this.checkInitialized();
    if (!this.contract) throw new Error("Contract not initialized");

    this.contract.on("VerificationRequested", callback);
  }

  onVerificationComplete(
    callback: (
      borrower: string,
      verified: boolean,
      attestationUID: string,
      verifyAt: bigint,
    ) => void,
  ) {
    this.checkInitialized();
    if (!this.contract) throw new Error("Contract not initialized");

    this.contract.on("VerificationComplete", callback);
  }

  onLoanApproved(
    callback: (borrower: string, amount: bigint, collateral: bigint) => void,
  ) {
    this.checkInitialized();
    if (!this.contract) throw new Error("Contract not initialized");

    this.contract.on("LoanApproved", callback);
  }

  onLoanRejected(callback: (borrower: string, reason: string) => void) {
    this.checkInitialized();
    if (!this.contract) throw new Error("Contract not initialized");

    this.contract.on("LoanRejected", callback);
  }

  onLoanRepaid(callback: (borrower: string, amount: bigint) => void) {
    this.checkInitialized();
    if (!this.contract) throw new Error("Contract not initialized");

    this.contract.on("LoanRepaid", callback);
  }

  onVerificationRevoked(
    callback: (
      borrower: string,
      requestId: string,
      attestationUID: string,
      timestamp: bigint,
    ) => void,
  ) {
    this.checkInitialized();
    if (!this.contract) throw new Error("Contract not initialized");

    this.contract.on("VerificationRevoked", callback);
  }

  // Remove all event listeners
  removeAllListeners() {
    if (this.contract) {
      this.contract.removeAllListeners();
    }
  }
}
